import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useBusiness } from './BusinessContext';
import { supabase } from '../lib/supabase';
import {
  createStripeCheckoutSession,
  createPlatformBillingPortalSession,
  handleStripeCheckoutSuccess,
} from '../lib/stripeCheckout.ts';
import { isStripeConfigured } from '../lib/stripe.ts';
import { getSiteUrl } from '../lib/siteConfig';

export interface StripeSubscriptionDetails {
  id: string;
  status: string;
  planName: string;
  planId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  renewalDate: string;
  amount: number;
  currency: string;
  interval: string;
  customerEmail: string;
}

export interface BusinessSubscription {
  id: string;
  business_id: string;
  subscription_status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled';
  subscription_last_checked: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
  active_plan_id?: string; // Track which plan is active
  stripeDetails?: StripeSubscriptionDetails | null; // Stripe subscription details
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
}

// Subscription plans will be loaded from database

interface SubscriptionContextType {
  subscription: BusinessSubscription | null;
  isActive: boolean;
  isLoading: boolean;
  error: string | null;
  plans: SubscriptionPlan[];
  refreshSubscription: () => Promise<void>;
  activateSubscription: (planId: string) => Promise<boolean>;
  /** Stripe Customer Billing Portal — pay open invoices / update card on existing platform subscription */
  openPlatformBillingPortal: (returnUrl: string) => Promise<boolean>;
  deactivateSubscription: () => Promise<boolean>;
  syncResourceSubscription: () => Promise<boolean>;
  fetchStripeDetails: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { selectedBusiness } = useBusiness();
  const [subscription, setSubscription] = useState<BusinessSubscription | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  const loadPlansFromDatabase = async () => {
    try {
      if (!supabase) {
        console.warn('Supabase client not available, skipping plans load');
        setPlans([]);
        return;
      }

      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id:plan_id, name:plan_name, price:plan_price, included_apps')
        .order('plan_price', { ascending: true });

      if (error) {
        console.error('Error loading subscription plans:', error);
        // If table doesn't exist or other error, just use empty array
        setPlans([]);
        return;
      }

      // Convert database format to SubscriptionPlan format.
      // Hosting rows (nexus-hosting) are billed separately — not platform plans.
      const formattedPlans: SubscriptionPlan[] = (data || [])
        .filter(plan => plan.id !== 'nexus-hosting')
        .map(plan => ({
        id: plan.id,
        name: plan.name,
        price: plan.price,
        interval: 'monthly' as const,
        features: plan.included_apps?.map((appId: string) => 
          appId.replace('-core-app', '').replace('-addon-app', '').replace('-', ' ')
        ) || []
      }));

      setPlans(formattedPlans);
      console.log('Successfully loaded subscription plans:', formattedPlans.length);
    } catch (err) {
      console.error('Error in loadPlansFromDatabase:', err);
      // Fallback to empty array on any error - don't let this crash the context
      setPlans([]);
    }
  };

  const fetchStripeDetails = async () => {
    if (!selectedBusiness || !subscription?.stripe_subscription_id) {
      return;
    }

    try {
      console.log('Fetching Stripe subscription details...');
      
      if (!supabase) {
        console.error('Supabase client not available');
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-stripe-subscription', {
        body: { businessId: selectedBusiness.id }
      });

      if (error) {
        console.error('Error fetching Stripe details:', error);
        return;
      }

      if (data?.subscription) {
        console.log('Stripe details received:', data.subscription);
        setSubscription(prev => prev ? {
          ...prev,
          stripeDetails: data.subscription
        } : null);
      } else {
        console.log('No Stripe subscription details found');
        setSubscription(prev => prev ? {
          ...prev,
          stripeDetails: null
        } : null);
      }
    } catch (err) {
      console.error('Error fetching Stripe details:', err);
    }
  };

  const loadSubscription = async () => {
    if (!selectedBusiness) {
      setSubscription(null);
      setIsActive(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch subscription data from database
      if (!supabase) {
        setError('Database connection not available');
        return;
      }
      
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id,
          subscription_status,
          subscription_last_checked,
          stripe_customer_id,
          stripe_subscription_id,
          active_plan_id,
          created_at,
          updated_at
        `)
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
        setError('Failed to load subscription status');
        return;
      }

      if (data) {
        const subscriptionData: BusinessSubscription = {
          id: data.id,
          business_id: data.id,
          subscription_status: data.subscription_status || 'inactive',
          subscription_last_checked: data.subscription_last_checked,
          stripe_customer_id: data.stripe_customer_id,
          stripe_subscription_id: data.stripe_subscription_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
          active_plan_id: data.active_plan_id
        };

        setSubscription(subscriptionData);
        
        // Check if subscription is active
        const isActiveSubscription = data.subscription_status === 'active' || 
                                     data.subscription_status === 'trialing';
        
        setIsActive(isActiveSubscription);

        // If we have a Stripe subscription ID, fetch the details
        if (data.stripe_subscription_id) {
          await fetchStripeDetails();
        }
      } else {
        // No subscription data found, create default inactive subscription
        const defaultSubscription: BusinessSubscription = {
          id: selectedBusiness.id,
          business_id: selectedBusiness.id,
          subscription_status: 'inactive',
          subscription_last_checked: null,
          stripe_customer_id: null,
          stripe_subscription_id: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setSubscription(defaultSubscription);
        setIsActive(false);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Failed to load subscription status');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSubscription = async () => {
    await loadSubscription();
  };

  const activateSubscription = async (planId: string): Promise<boolean> => {
    if (!selectedBusiness) {
      setError('No business loaded. Please refresh the page or log in again.');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check if Stripe is configured
      if (!isStripeConfigured()) {
        setError('Stripe is not configured. Please contact support.');
        return false;
      }

      // Find the plan details from database
      const selectedPlan = plans.find(plan => plan.id === planId);
      console.log(`Initiating Stripe checkout for business ${selectedBusiness.id} with plan ${planId}`);
      console.log(`Selected plan: ${selectedPlan?.name} - $${selectedPlan?.price}/month`);

      // Get user email from Supabase auth
      if (!supabase) {
        setError('Database connection not available');
        return false;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        setError('User email not found');
        return false;
      }

      // Create Stripe checkout session or update existing subscription
      const result = await createStripeCheckoutSession({
        planId,
        businessId: selectedBusiness.id,
        customerEmail: user.email,
        successUrl: `${getSiteUrl()}/subscription-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${getSiteUrl()}/admin-subscription-management`
      });

      if (!result.success && result.needsBillingPortal) {
        setError(
          result.message ||
            'Your current subscription needs a payment first. Use “Update billing & pay invoice” on this page, then try again if you want to change plans.',
        );
        return false;
      }

      // If plan was updated directly (no checkout needed), refresh subscription data
      if (result.success && result.planUpdated) {
        console.log('Plan updated directly, refreshing subscription data...');
        await loadSubscription();
      }

      return true;
    } catch (err) {
      console.error('Error creating Stripe checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to create checkout session');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const openPlatformBillingPortal = async (returnUrl: string): Promise<boolean> => {
    if (!selectedBusiness?.id) {
      setError('No business loaded. Please refresh the page or log in again.');
      return false;
    }
    try {
      setIsLoading(true);
      setError(null);
      const session = await createPlatformBillingPortalSession(selectedBusiness.id, returnUrl);
      if (!session?.url) {
        setError('Could not open billing. Ensure Stripe Customer Portal is configured and this business has billing on file.');
        return false;
      }
      window.location.assign(session.url);
      return true;
    } catch (e) {
      console.error('openPlatformBillingPortal', e);
      setError(e instanceof Error ? e.message : 'Failed to open billing portal');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

    const deactivateSubscription = async (): Promise<boolean> => {
    if (!selectedBusiness) return false;

    try {
      setIsLoading(true);
      setError(null);

      if (!supabase) {
        setError('Database connection not available');
        return false;
      }

      console.log(`Deactivating subscription for business ${selectedBusiness.id}`);
      
      // Update the business record in the database
      const { error: businessError } = await supabase
        .from('businesses')
        .update({
          subscription_status: 'inactive',
          subscription_last_checked: new Date().toISOString(),
          active_plan_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedBusiness.id);

      if (businessError) {
        console.error('Error updating business subscription in database:', businessError);
        setError('Failed to deactivate subscription');
        return false;
      }

      // Update all resources for this business to have inactive subscription status
      const { error: resourcesError } = await supabase
        .from('resources')
        .update({
          subscription_status: 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('business_id', selectedBusiness.id);

      if (resourcesError) {
        console.error('Error updating resources subscription status:', resourcesError);
        // Don't fail the entire operation, just log the error
        console.warn('Business subscription deactivated but some resources may not be updated');
      } else {
        console.log(`✅ Updated subscription status for all resources in business ${selectedBusiness.id}`);
      }

      // Update local state
      const updatedSubscription: BusinessSubscription = {
        id: selectedBusiness.id,
        business_id: selectedBusiness.id,
        subscription_status: 'inactive',
        subscription_last_checked: new Date().toISOString(),
        stripe_customer_id: null,
        stripe_subscription_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setSubscription(updatedSubscription);
      setIsActive(false);

      console.log(`✅ Successfully deactivated subscription`);
      return true;
    } catch (err) {
      console.error('Error deactivating subscription:', err);
      setError('Failed to deactivate subscription');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const syncResourceSubscription = async (): Promise<boolean> => {
    if (!selectedBusiness) return false;

    try {
      setIsLoading(true);
      setError(null);

      if (!supabase) {
        setError('Database connection not available');
        return false;
      }

      console.log(`Syncing resource subscription status for business ${selectedBusiness.id}`);
      
      // Update all resources for this business to match the business subscription status
      const { error } = await supabase
        .from('resources')
        .update({
          subscription_status: subscription?.subscription_status || 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('Error syncing resource subscription status:', error);
        setError('Failed to sync resource subscription status');
        return false;
      }

      console.log(`✅ Successfully synced resource subscription status for business ${selectedBusiness.id}`);
      return true;
    } catch (err) {
      console.error('Error syncing resource subscription status:', err);
      setError('Failed to sync resource subscription status');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
    // Only load plans if we have a business selected
    if (selectedBusiness?.id) {
      loadPlansFromDatabase();
    }
  }, [selectedBusiness]);

  const value: SubscriptionContextType = {
    subscription,
    isActive,
    isLoading,
    error,
    plans,
    refreshSubscription,
    activateSubscription,
    openPlatformBillingPortal,
    deactivateSubscription,
    syncResourceSubscription,
    fetchStripeDetails,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 